import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import { Box, CircularProgress } from "@mui/material/";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logos/QD_icon_W.png";
import brandDark from "assets/images/logo-ct-dark.png";

// Material Dashboard 2 React layouts
import Warbler1Dash from "layouts/warbler_q123k5";
import Checkout from "layouts/checkout";
import Orders from "layouts/orders";
import Update from "layouts/update";
import SignIn from "layouts/authentication/sign-in";
import Dashboard from "layouts/dashboard";
import ResetPassword from "layouts/authentication/reset-password/cover";
import OrderPage from "layouts/orderpage";
import Edit from "layouts/edit";
import Tracking from "layouts/tracking";
import Directory from "layouts/directory";
// import Inventory from "layouts/inventory";

// Firebase imports
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./Firebase";

// Cache Clearing
import withClearCache from "./clearCache";

function MainApp() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    cart,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const [cartCount, setCartCount] = useState(false);

  const [user] = useAuthState(auth);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (cart.length > 0) {
      setCartCount(true);
    } else {
      setCartCount(false);
    }
  }, [cart]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        shopping_cart
      </Icon>
      {cartCount ? (
        <MDTypography variant="caption">
          <sub>{cart.length}</sub>
        </MDTypography>
      ) : (
        ""
      )}
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Material Dashboard 2"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route exact path="/" element={user ? <Warbler1Dash /> : <Checkout />} />
          <Route path="/checkout" element={<Navigate to="/checkout" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "loading" && (
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      )}
      {layout === "dashboard" && (
        <Sidenav
          color={sidenavColor}
          brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
          brandName="QD Storefront"
          routes={routes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        <Route exact path="*" element={!user ? <SignIn /> : <Orders />} />
        <Route exact path="/authentication/sign-in" element={<SignIn />} />
        <Route exact path="/authentication/reset" element={<ResetPassword />} />
        <Route exact path="/dashboard" element={!user ? <SignIn /> : <Dashboard />} />
        <Route exact path="/update" element={!user ? <SignIn /> : <Update />} />
        <Route exact path="/orderpage" element={!user ? <SignIn /> : <OrderPage />} />
        <Route exact path="/edit" element={!user ? <SignIn /> : <Edit />} />
        <Route exact path="/tracking" element={!user ? <SignIn /> : <Tracking />} />
        <Route exact path="/directory" element={!user ? <SignIn /> : <Directory />} />
        {/* <Route exact path="/inventory" element={!user ? <SignIn /> : <Inventory />} /> */}
      </Routes>
    </ThemeProvider>
  );
}

const ClearCacheComponent = withClearCache(MainApp);
function App() {
  return <ClearCacheComponent />;
}

export default App;
